/**
 * Recipient rows for the contact modal. Pure — display names trimmed/joined,
 * deduped by candidateId, blank-named candidates skipped (a chip with no text
 * is noise). The rows deliberately carry NO email address: the server resolves
 * every address from the owned search snapshot, so a client-side address can
 * never reach the transport.
 */
import type { MapsCandidate, MapsContactRecipientResult, MapsContactRecipientStatus } from '../models/maps.types';

export interface ContactRecipient {
    candidateId: string;
    name: string;
}

/** A recipient row after a send attempt — drives the modal's results view. */
export interface ContactRecipientRow extends ContactRecipient {
    /** null before any send has been attempted for this recipient. */
    status: MapsContactRecipientStatus | null;
    error: string | null;
}

export function BuildRecipients(candidates: readonly MapsCandidate[]): ContactRecipient[] {
    const seen = new Set<string>();
    const rows: ContactRecipient[] = [];
    for (const c of candidates) {
        if (seen.has(c.candidateId)) continue;
        seen.add(c.candidateId);
        const name = [c.firstName?.trim(), c.lastName?.trim()].filter(Boolean).join(' ');
        if (name.length === 0) continue;
        rows.push({ candidateId: c.candidateId, name });
    }
    return rows;
}

/**
 * Merge per-recipient send results onto the recipient chips (pure). A
 * recipient with no matching result stays status:null — the send path never
 * saw it (e.g. it was deselected server-side); the row renders unbadged
 * rather than inventing an outcome.
 */
export function MergeRecipientResults(
    recipients: readonly ContactRecipient[],
    results: readonly MapsContactRecipientResult[]
): ContactRecipientRow[] {
    const byId = new Map(results.map((r) => [r.candidateId, r]));
    return recipients.map((recipient) => {
        const result = byId.get(recipient.candidateId);
        return {
            ...recipient,
            status: result?.status ?? null,
            error: result?.error ?? null,
        };
    });
}
